import React from 'react';
import { api } from '../api.js';
import { mapTasks } from '../mapping.js';
import { applyServerData, TASK_SUMMARY, TASKS } from './data.jsx';

/**
 * 내 업무 목록·요약과 완료 체크 — 홈과 '내 업무' 화면이 같이 쓴다(계획 문서 5.1 P1-4).
 * 체크는 먼저 화면에 보이고 서버에 저장한다. 실패하면 서버 상태로 되돌아간다.
 * 체크를 풀면 체크하기 전에 서버가 줬던 상태·진행률로 돌린다(처음부터 완료였던 업무는 '진행').
 * 저장 뒤 목록·요약을 서버에서 다시 받는다 — 건수는 화면에서 세지 않는다.
 */
export function useTasks(onError) {
  const [tasks, setTasks] = React.useState(TASKS);
  const [summary, setSummary] = React.useState(TASK_SUMMARY);
  const [shown, setShown] = React.useState({});          // id → 저장 중인 동안 먼저 보이는 체크 상태
  const before = React.useRef({});                         // id → 체크하기 전 서버 상태·진행률(이번 화면에서 체크한 것)

  const reload = React.useCallback(() => Promise.all([api.get('/api/v1/tasks'), api.get('/api/v1/tasks/summary')])
    .then(([list, sum]) => {
      const mapped = mapTasks(list.tasks);
      setTasks(mapped); setSummary(sum);
      applyServerData({ tasks: mapped, taskSummary: sum });   // 다른 화면이 다시 그릴 때 같은 값을 보게
    }), []);

  const isDone = t => shown[t.id] ?? t.status === 'done';
  const toggle = t => {
    const next = !isDone(t);
    let body;
    if (next) {
      if (t.status !== 'done') before.current[t.id] = { status: t.status, progress: t.progress };
      body = { status: 'done' };
    } else {
      const orig = before.current[t.id];
      body = orig ? { status: orig.status, progress: orig.progress } : { status: 'doing' };
    }
    setShown(s => ({ ...s, [t.id]: next }));
    api.patch(`/api/v1/tasks/${t.id}`, body)
      .then(() => { if (!next) delete before.current[t.id]; return reload(); })
      .catch(e => onError?.(e.message))
      .finally(() => setShown(s => { const rest = { ...s }; delete rest[t.id]; return rest; }));
  };
  return { tasks, summary, isDone, toggle, reload };
}
